import React, { useEffect, useState, memo, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  toggleMsgLoader,
  toggleWidget,
  Widget,
  addResponseMessage,
  dropMessages
} from 'react-chat-widget';
import 'react-chat-widget/lib/styles.css';
import { useSelector } from 'react-redux';
import * as commonService from "../../../utility/CommonService";
import { getAIChatWebSocketUrl } from '../../../utility/websocketUrls';
import aiProfilePic from "../../../assets/custom-styles/images/icons/aiProfile.svg";
import Markdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { capitalizeFirstLetter } from "../../../utility/utility";
import { ContentCopy } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import CloseIcon from "@mui/icons-material/Close";
import AIIcon from './aiIcon';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';

// Custom Markdown component for assistant messages
const MarkdownResponse = ({ content }) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async (text) => {
    setIsCopied(true);
    await navigator.clipboard.writeText(text);
    setTimeout(() => setIsCopied(false), 700);
  }

  return (
    <Markdown
      children={content}
      components={{
        code: ({node, inline, className, children, ...props}) => {
          const match = /language-(\w+)/.exec(className || '')
          return !inline && match ? (
            <div className="chat-code-block">
              <div className="chat-code-block-header">
                <div className="code-block-language">{capitalizeFirstLetter(match[1])}</div>
                <button className="code-copy-button" onClick={async () => {
                  await handleCopy(children)
                }}>
                  <div className="copy-button-text">
                    <SvgIcon className="copy-icon">
                      <ContentCopy />
                    </SvgIcon>
                    {isCopied ? "Copied": "Copy Code"}
                  </div>
                </button>
              </div>
              <SyntaxHighlighter
                style={prism}
                customStyle={{
                  padding: "6px 6px",
                  background: "#F9F9F9",
                  borderBottomLeftRadius: "8px",
                  borderBottomRightRadius: "8px",
                  marginTop: 0,
                  marginBottom: 0
                }}
                children={String(children).replace(/\n$/, '')}
                language={match[1]}
                {...props}
              />
            </div>
          ) : (
            <code className={className} {...props}>
              {children}
            </code>
          )
        },
      }}
    />
  );
}

// Main Chat Interface
const ChatInterface = memo(({projectId, handleShowAIChat, chatRef}) => {
  const [messages, setMessages] = useState([]);
  const { user } = useSelector((state) => state.authReducer);
  const chatInitialized = useRef(false);
  const streamingMessageRef = useRef(null);
  const markdownContainerRef = useRef(null);

  // Initialize chat on first render
  useEffect(() => {
    if (!chatRef.current) {
      chatRef.current = true;

      if (!chatInitialized.current) {
        initializeChat();
      }
    }
  }, [chatRef]);

  // Function to initialize/reset chat
  const initializeChat = () => {
    // Clear existing messages
    dropMessages();
    setMessages([]);
    streamingMessageRef.current = null;
    markdownContainerRef.current = null;

    // Add welcome message
    const welcomeMessage = {
      id: "welcome-message",
      content: "Welcome! Start a conversation with the AI assistant.",
      role: 'assistant',
      timestamp: new Date().toLocaleTimeString(),
    };

    addResponseMessage(welcomeMessage.content);

    // Add message to our state too
    setMessages([welcomeMessage]);

    toggleWidget();
    chatInitialized.current = true;
  }

  // Helper function to prepare the container for streaming updates
  const prepareStreamingContainer = () => {
    // Find the last response message (which should be our new empty message)
    const msgContainer = document.querySelector('.rcw-messages-container');
    if (!msgContainer) return false;

    const responseMessages = msgContainer.querySelectorAll('.rcw-response');
    if (responseMessages.length === 0) return false;

    const lastResponse = responseMessages[responseMessages.length - 1];
    if (!lastResponse) return false;

    // Find or create the text container
    const textContainer = lastResponse.querySelector('.rcw-message-text');
    if (!textContainer) return false;

    // Create a container for our markdown content if it doesn't exist
    let markdownContainer = textContainer.querySelector('.markdown-container');
    if (!markdownContainer) {
      markdownContainer = document.createElement('div');
      markdownContainer.className = 'markdown-container';
      textContainer.innerHTML = ''; // Clear any existing content
      textContainer.appendChild(markdownContainer);
    }

    // Store references to these elements for later updates
    streamingMessageRef.current = lastResponse;
    markdownContainerRef.current = markdownContainer;

    return true;
  }

  // Update streaming message content
  const updateStreamingContent = (content) => {
    // If we don't have references to our containers yet, try to get them
    if (!streamingMessageRef.current || !markdownContainerRef.current) {
      if (!prepareStreamingContainer()) return;
    }

    // Use ReactDOM.render to update the content
    ReactDOM.render(
      <MarkdownResponse content={content} />,
      markdownContainerRef.current
    );

    // Scroll to bottom
    const msgContainer = document.querySelector('.rcw-messages-container');
    if (msgContainer) {
      msgContainer.scrollTop = msgContainer.scrollHeight;
    }
  }

  // Handle new user message
  const handleNewUserMessage = (newMessage) => {
    const userMessage = {
      id: `user-${Date.now()}`,
      content: newMessage,
      role: 'user',
      timestamp: new Date().toLocaleTimeString(),
    };

    // Update messages state
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);

    // Get AI response
    getAiResponse(updatedMessages);
  };

  // Refresh/clear the chat
  const refreshChat = () => {
    // Reset state
    chatInitialized.current = false;

    // Initialize chat again
    initializeChat();
  }

  // Get AI response via WebSocket
  const getAiResponse = async (chatMessages) => {
    toggleMsgLoader();

    // Create new websocket connection
    const wsUrl = getAIChatWebSocketUrl(user.uid, projectId);
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      ws.send(JSON.stringify(chatMessages))
    }

    // Create empty response message
    const responseId = `assistant-${Date.now()}`;

    const emptyMessage = {
      id: responseId,
      content: '',
      role: 'assistant',
      timestamp: new Date().toLocaleTimeString(),
    };

    // Add empty message to the UI
    addResponseMessage(' '); // Add a space to create the message bubble

    // Add empty message to state
    setMessages(prev => [...prev, emptyMessage]);

    // Reset references for new message
    streamingMessageRef.current = null;
    markdownContainerRef.current = null;

    // Prepare the container for streaming updates
    setTimeout(() => {
      prepareStreamingContainer();
    }, 0);

    // Stream buffer to accumulate response
    let streamBuffer = '';

    // Handle incoming messages
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === 'token') {
        // Accumulate tokens in buffer
        streamBuffer += data.content || '';

        // Update message content in state
        setMessages(prev => {
          return prev.map(msg => {
            if (msg.id === responseId) {
              return { ...msg, content: streamBuffer };
            }
            return msg;
          });
        });

        // Update UI directly
        updateStreamingContent(streamBuffer);
      } else if (data.type === 'complete') {
        // Add final token if any
        if (data.content) {
          streamBuffer += data.content;
        }

        // Update final message in state
        setMessages(prev => {
          return prev.map(msg => {
            if (msg.id === responseId) {
              return { ...msg, content: streamBuffer };
            }
            return msg;
          });
        });

        // Update UI one last time
        updateStreamingContent(streamBuffer);

        // Message is complete
        toggleMsgLoader();
        streamingMessageRef.current = null;
        markdownContainerRef.current = null;
        ws.close();
      }
    };

    // Handle errors
    ws.onerror = (error) => {
      toggleMsgLoader();
      commonService.forError("Could not connect to Ludis AI.", "Error");

      // Remove the partial message from state
      setMessages(prev => prev.filter(msg => msg.id !== responseId));

      // Try to remove from UI if we have a reference
      if (streamingMessageRef.current) {
        streamingMessageRef.current.remove();
      } else {
        // Try to find and remove the last response message
        const msgContainer = document.querySelector('.rcw-messages-container');
        if (msgContainer) {
          const responseMessages = msgContainer.querySelectorAll('.rcw-response');
          const lastResponse = responseMessages[responseMessages.length - 1];
          if (lastResponse) {
            lastResponse.remove();
          }
        }
      }

      streamingMessageRef.current = null;
      markdownContainerRef.current = null;
      ws.close();
    };
  };

  return (
    <div className="chat_interface_wrapper">
      <div className="dev_tool_header">
        <div className='left-group'>
          <div className='dev-tools-icon'>
            <AIIcon/>
          </div>
          <div className='clear-conversation-container' onClick={refreshChat}>
            <RotateLeftIcon className='clear-conversation-icon'/>
            <p className='clear-conversation'>Clear conversation</p>
          </div>
        </div>
        <CloseIcon className="float-right" onClick={handleShowAIChat} />
      </div>
      <Widget
        handleNewUserMessage={handleNewUserMessage}
        title=""
        subtitle=""
        profileAvatar={aiProfilePic}
        showBadge={false}
      />
    </div>
  );
});

export default ChatInterface;